import React, { Dispatch, SetStateAction } from 'react';
import { useFormContext } from 'react-hook-form';
import cn from 'classnames';

import { FormContainerTextfield } from '@/components/common/Form/Container/Textfield';
import { FormLabel } from '@/components/common/Form/Label';
import { FormLayoutFieldset } from '@/components/common/Form/Layout/Fieldset';
import { FormRadioButtonItem } from '@/components/common/Form/RadioButtonItem';
import { ToggleLabelButton } from '@/components/common/Form/ToggleLabelButton';
import { LayoutColumDivider } from '@/components/common/Layout/ColumDivider';
import { ProfileImageSquare } from '@/components/common/OtherComponents/ProfileImageSquare';
import { TitleSectionTitle } from '@/components/common/Title/SectionTitle';
import ImgIcon from '@/assets/images/OtherComponents/Square-thumbnail.svg';
import { ImagePhoto } from '@/utils/api-client';

type TSectionProps = {
  id?: string;
  original: {
    gender?: number;
    photo?: ImagePhoto;
  };
};
export const ResumeFormSectionProfile = ({
  id,
  iconSrc,
  setIconSrc,
  setIconState,
  original,
}: TSectionProps & {
  iconSrc: string | undefined;
  setIconSrc: Dispatch<SetStateAction<string | undefined>>;
  setIconState: Dispatch<SetStateAction<'stay' | 'change' | 'delete'>>;
}): React.ReactElement => {
  const methods = useFormContext();
  const gender_check = methods.watch('gender_check');
  const photo_check = methods.watch('photo_check');

  const onIconSave = (res: string) => {
    setIconState('change');
    setIconSrc(res);
  };

  const onIconClick = async () => {
    if (!iconSrc) return;
    setIconState('delete');
    setIconSrc(undefined);
  };

  return (
    <div id={id}>
      <TitleSectionTitle>
        <div className="flex items-center">
          <span className="mr-4">プロフィール</span>
        </div>
      </TitleSectionTitle>
      <LayoutColumDivider
        className={['mbx-utils-stack-v--16 sm:mt-32', 'flex items-center justify-center']}
      >
        <>
          <FormLayoutFieldset>
            <FormLabel label="氏名" type="small"></FormLabel>
            <FormContainerTextfield name="name" readOnly={true} className="mkt_mask_items" />
          </FormLayoutFieldset>
          <FormLayoutFieldset>
            <FormLabel label="ふりがな" type="small"></FormLabel>
            <FormContainerTextfield name="furigana" readOnly={true} className="mkt_mask_items" />
          </FormLayoutFieldset>
          <ToggleLabelButton name="gender_check" className="mb-16 mt-24" />
          <FormLayoutFieldset>
            <FormLabel label="性別" type="small"></FormLabel>
            {gender_check ? (
              <div className="mbx-utils-stack-h--54 flex mkt_mask_items">
                <FormRadioButtonItem label="男" name="gender" value="2"></FormRadioButtonItem>
                <FormRadioButtonItem label="女" name="gender" value="1"></FormRadioButtonItem>
                <FormRadioButtonItem
                  label="選択しない"
                  name="gender"
                  value="0"
                ></FormRadioButtonItem>
              </div>
            ) : (
              <div className="mbx-utils-stack-h--54 flex mkt_mask_items opacity-40 pointer-events-none">
                <div>
                  <label className="mbx-formRadio relative">
                    <input
                      type="radio"
                      value="2"
                      readOnly
                      checked={original.gender && original.gender == 2 ? true : false}
                    />
                    <span className="text-body_1 py-12 pl-32">男</span>
                  </label>
                </div>
                <div>
                  <label className="mbx-formRadio relative">
                    <input
                      type="radio"
                      value="1"
                      readOnly
                      checked={original.gender && original.gender == 1 ? true : false}
                    />
                    <span className="text-body_1 py-12 pl-32">女</span>
                  </label>
                </div>
                <div>
                  <label className="mbx-formRadio relative">
                    <input
                      type="radio"
                      value="0"
                      readOnly
                      checked={original.gender && original.gender == 0 ? true : false}
                    />
                    <span className="text-body_1 py-12 pl-32">選択しない</span>
                  </label>
                </div>
              </div>
            )}
          </FormLayoutFieldset>
          <FormLayoutFieldset>
            <FormLabel label="生年月日" type="small"></FormLabel>
            <FormContainerTextfield name="birth" readOnly={true} className="mkt_mask_items" />
          </FormLayoutFieldset>
        </>
        <>
          <div className="flex flex-col items-center">
            <ToggleLabelButton name="photo_check" className="mb-16 mt-24" />
            <h1 className="text-12_16 font-bold py-6 mb-4 text-center">証明写真</h1>
            <ProfileImageSquare
              src={iconSrc}
              onIconSave={onIconSave}
              className={cn('mkt_mask_items w-104', { hidden: !photo_check })}
            />
            {!photo_check && (
              <div className="mbx-profileImageSquare mbx-profileImageSquare--preview opacity-40">
                <img
                  src={original?.photo?.f_url ? original?.photo?.f_url : ImgIcon}
                  className="w-auto"
                  style={{ height: '144px' }}
                />
              </div>
            )}
            <div className="pt-16 text-center">
              <p className="text-caption_2 text-gray-700">横208px × 縦288px推奨、最大5MB</p>
            </div>
            {photo_check && iconSrc && (
              <div className="pt-16 text-center">
                <div className="base-button isTextLink" onClick={onIconClick}>
                  <p className="button size-s theme-link flex items-center">
                    <i className="mbx-icon mbx-icon-Trash mr-2"></i>
                    <span className="font-bold label">画像を削除</span>
                  </p>
                </div>
              </div>
            )}
          </div>
        </>
      </LayoutColumDivider>
    </div>
  );
};
